/// <amd-module name="Test/Analytics/Common/Types" />
import { FilterState } from "Test/common-util/Contracts/Filters";
import * as TCMContracts from "Test/common-util/Contracts/Test";
import * as RMContracts from "Test/Release/Client/Contracts/Release";

export interface ITreeItem {
    nodeType?: TreeNodeType;
    depth?: number;
    expanded?: boolean;
    key?: any;
}

export enum ViewType {
    NoTestDataView,
    ReportView,
    TestInsightsReportView,
    ErrorView,
    DataNotReadyView
}

export enum TreeNodeType {
    leaf = 1,
    group,
    showMore
}

export enum ColumnIndices {
    Test = 1,
    Passrate,
    AvgDuration,
    TotalCount,
    FailedCount,
    PassedCount,
    InconclusiveCount,
    AbortedCount,
    NotExecutedCount,
    ErrorCount,
    NotImpactedCount,
    FlakyCount
}

export interface IDetailedListItem extends ITreeItem {
    itemkey: number | string;
    itemName: string;
    passPercentage: number | string;
    avgDuration: number | string;
    avgDurationAriaLabel: string;
    totalCount: number;
    failedCount: number;
    passedCount: number;
    inconclusiveCount: number;
    abortedCount: number;
    notExecutedCount: number;
    notImpactedCount: number;
    errorCount: number;
    parentItemKey?: number | string;
    flakyCount: number;
}

export interface IDetailedListColumn {
    column: ColumnIndices;
    sortOrder: SortOrder;
}

export enum SortOrder {
    Ascending = 1,
    Descending
}

export enum GridViewDisplayType {
    Loading,
    Initialized,
    Initializing
}

export interface IConfigurationProps {
    options: { [key: number]: string };
}

export enum Filter {
    // Defining these as strings as filter fieldName is accepted as string.
    Workflow = "Workflow",
    Branch = "Branch",
    Environment = "Environment",
    TestRun = "TestRun",
    Container = "Container",
    Owner = "Owner",
    Outcome = "Outcome"
}

export interface ITestContext {
    testIdentifier: string;
    testName: string;
}

export interface INextDataPageToken {
    token: string;
}

export interface IDetailedListData extends IReportData {
    detailedListItems: IDetailedListItem[];
    nextPageToken?: INextDataPageToken;
}

export interface IDetailedListGroupItem {
    groupItem: IDetailedListItem;
    children: IDetailedListItem[];
    nextPageToken?: INextDataPageToken;
}

export interface ICardMetricsData extends IReportData {
    passPercentage: number | string;
    passedCount: number;
    failedCount: number;
    totalCount: number;
    notExecutedCount: number;
    notImpactedCount: number;
    totalFailingTestsCount: number;
}

export interface IDetailedListGroupData extends IReportData {
    detailedListGroupItem: IDetailedListGroupItem;
}

export enum DataSourceTable {
    TestRun = 1,
    TestResult,
    TestResultDaily
}

export interface IODataQueryResponse {
    value: any;
    "@odata.nextLink": string;
    isCachedData: boolean;
}

export enum GroupBy {
    None = 1,
    Branch = 2,
    Container = 3,
    Environment = 4,
    Owner = 5,
    Outcome = 6,
    Priority = 7,
    TestRun = 8
}

export enum TrendBy {
    Days = 1,
    Weeks = 2
}

export enum Period {
    Days_7 = 1,
    Days_14 = 2,
    Days_30 = 3
}

export enum Workflow {
    Build = 1,
    Release = 2
}

export enum TestOutcome {
    Failed = 1,
    Passed = 2,
    Inconclusive = 3,
    Aborted = 4,
    NotExecuted = 5,
    Error = 6,
    NotImpacted = 7,
    Total = 8
}

export enum LoadType {
    PageLoad, // New page loading (1st time page loading).
    ReportConfigurationChange, // Chart loading due to confValue change.
    ChartMetricChange // Chart loading due to chart pivot change.
}

export interface IReportConfiguration {
    groupBy: GroupBy;
    trendBy: TrendBy;
    period: Period;
    outcomes: TestOutcome[];
    configuredFilters: FilterState;
}

export interface IReportData {
    confValues?: IReportConfiguration;
    isCachedData?: boolean;
}

export interface ICardMetricsData extends IReportData {
    passPercentage: number | string;
    passedCount: number;
    failedCount: number;
    totalCount: number;
    notExecutedCount: number;
    notImpactedCount: number;
}
export interface IFailedCardMetricData extends IReportData {
    totalFailingTestsCount: number;
    totalFailedResultsCount: number;
}

export interface IReportComponentProps {
    instanceId?: string;
}

export enum Metric {
    ResultCountAndPassRate = 0,
    ResultCount = 1,
    AvgDuration = 2
}

export interface ITestReportContext {
    contextType: TCMContracts.TestResultsContextType;
    definitionId: number;
}

export interface IODataQueryResponse {
    value: any;
    "@odata.nextLink": string;
    isCachedData: boolean;
}

export interface IFilterMetadataSource {
    queryFilterFieldValues(
        testReportContext: ITestReportContext,
        confValues: IReportConfiguration,
        filterBy: Filter
    ): Promise<TCMContracts.FieldDetailsForTestResults>;
    queryReleaseDefinition(releaseDefinitionId: number): Promise<RMContracts.ReleaseDefinition>;
    queryReleaseDefinitionsByBuildArtifact(buildDefinitionId: number): Promise<RMContracts.ReleaseDefinition[]>;
}

export interface ITestAnalyticsLinkData {
    testAnalyticsLink: string;
    testAnalyticsLinkMessage: string;
}

export interface IPassRateChartSource {
    getTestAnalyticsLinkData(buildDefinitionId: number): Promise<ITestAnalyticsLinkData>;
}

export interface IFailedTestsSource {
    getTestAnalyticsLinkData(testReportContext: ITestReportContext): Promise<ITestAnalyticsLinkData>;
}
